import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../../../Data/UserContext";
import { LEAGUE_VIEWER_USER_ID } from "../lib/constants";
import { useLeaguesDataSource } from "../lib/LeaguesDataSource";
import { leagueStatus } from "../lib/leagueFormat";
import { isLeaguesDemoPath, LEAGUES_DEMO_PATH, leagueJoinPath } from "../lib/leagueGamePlayUrl";
import { useActiveLeagueMode } from "../lib/leagueModeStore";
import {
    findLeague as findPrototypeLeague,
    getLeaguesSnapshot as getPrototypeLeaguesSnapshot,
    subscribeToLeagues as subscribeToPrototypeLeagues,
} from "../lib/leagueStore";
import type { League, LeagueStatus, LeaguesState } from "../lib/types";

export function useLeaguesState(): LeaguesState {
    const dataSource = useLeaguesDataSource();
    return useSyncExternalStore(dataSource.subscribe, dataSource.getSnapshot, dataSource.getSnapshot);
}

/**
 * Base path for league links. The demo lives under /leaguesdemo so its hub,
 * detail and join links stay inside the demo island; everything else is /leagues.
 * Derived from the URL (and active prototype league) so it also works for league-mode
 * surfaces rendered outside the Leagues route subtree (Layout overlays, game pages).
 */
export function useLeagueBasePath(): string {
    const { pathname } = useLocation();
    const activeId = useActiveLeagueMode();
    if (isLeaguesDemoPath(pathname)) return LEAGUES_DEMO_PATH;
    if (activeId && findPrototypeLeague(activeId)) return LEAGUES_DEMO_PATH;
    return "/leagues";
}

/**
 * Resolve a league from the active data source, falling back to the prototype store.
 * Needed when demo leagues deep-link into `/squares` / `/trader` / etc. and leave the
 * `/leaguesdemo` provider — League mode header + wallet override still need the seat.
 */
export function useLeague(leagueId: string | undefined): League | null {
    const { leagues } = useLeaguesState();
    // Always subscribe: outside /leaguesdemo the active source is API-only.
    const protoSnap = useSyncExternalStore(
        subscribeToPrototypeLeagues,
        getPrototypeLeaguesSnapshot,
        getPrototypeLeaguesSnapshot,
    );
    return useMemo(() => {
        if (!leagueId) return null;
        return (
            leagues.find((league) => league.id === leagueId) ??
            protoSnap.leagues.find((league) => league.id === leagueId) ??
            null
        );
    }, [leagues, protoSnap, leagueId]);
}

export function useLeagueByCode(code: string | undefined): League | null {
    const { leagues } = useLeaguesState();
    return useMemo(() => {
        const needle = String(code ?? "").trim().toUpperCase();
        if (!needle) return null;
        return leagues.find((league) => league.code.toUpperCase() === needle) ?? null;
    }, [leagues, code]);
}

function liveViewerUserId(userId: string | number | null | undefined): number | null {
    const parsed = Number(userId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * The viewer's display identity. Real leagues use the logged-in account id so coin
 * lookups match `members[].isViewer`. The `/leaguesdemo` island (and demo leagues
 * opened on game routes) keep the prototype id.
 */
export function useLeagueViewer(): { name: string; avatar: string | null; xpWeekCrown: string | null; userId: number } {
    const { user } = useUser();
    const dataSource = useLeaguesDataSource();
    const activeId = useActiveLeagueMode();
    return useMemo(() => {
        const name = String(user?.player_name ?? user?.username ?? "").trim() || "Player";
        const liveId = liveViewerUserId(user?.userId);
        const prototypeSeat =
            dataSource.mode === "prototype" || Boolean(activeId && findPrototypeLeague(activeId));
        return {
            name,
            avatar: user?.avatar ?? null,
            xpWeekCrown: user?.xp_week_crown ?? null,
            userId: prototypeSeat || liveId == null ? LEAGUE_VIEWER_USER_ID : liveId,
        };
    }, [
        dataSource.mode,
        activeId,
        user?.player_name,
        user?.username,
        user?.avatar,
        user?.xp_week_crown,
        user?.userId,
    ]);
}

/** Re-renders on an interval so countdowns and "2m ago" labels stay honest. */
export function useNowTicker(intervalMs = 1000): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}

export function useLeagueStatus(league: League | null): LeagueStatus | null {
    const now = useNowTicker(5000);
    return useMemo(() => (league ? leagueStatus(league, now) : null), [league, now]);
}

/** Share URL for the copy-link action — carries `invitedBy` so previews name the sender. */
export function useLeagueShareLink(league: League | null): string {
    const basePath = useLeagueBasePath();
    const { user } = useUser();
    const inviterPlayerId = String(
        (user as { playerId?: string | null; player_id?: string | null } | null)?.playerId ??
            (user as { player_id?: string | null } | null)?.player_id ??
            "",
    ).trim();
    return useMemo(() => {
        if (!league) return "";
        const origin = typeof window !== "undefined" ? window.location.origin : "https://cricket8.com";
        return `${origin}${leagueJoinPath(league.code, basePath, inviterPlayerId)}`;
    }, [league?.code, basePath, inviterPlayerId]);
}

/** Copy-to-clipboard with a short-lived "Copied" confirmation. */
export function useCopyToClipboard(resetMs = 2200): { copied: boolean; copy: (value: string) => void } {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return;
        const id = setTimeout(() => setCopied(false), resetMs);
        return () => clearTimeout(id);
    }, [copied, resetMs]);

    const copy = useCallback((value: string) => {
        if (!value) return;
        const fallback = () => {
            const el = document.createElement("textarea");
            el.value = value;
            el.setAttribute("readonly", "");
            el.style.position = "fixed";
            el.style.opacity = "0";
            document.body.appendChild(el);
            el.select();
            try {
                document.execCommand("copy");
            } catch {
                /* Nothing else to try. */
            }
            document.body.removeChild(el);
        };
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(value).catch(fallback);
        } else {
            fallback();
        }
        setCopied(true);
    }, []);

    return { copied, copy };
}
